import { FormattedMessage } from 'react-intl';
import Button from '../Button';
import { useCart } from '@/hooks';
import { getPrice } from '@/utils/getPrice';
import useLocalStorage from '@/hooks/useLocalStorage';
import { useFormContext, useWatch } from 'react-hook-form';
import { OrderSendForm } from '@/types/order';
import useToggle from '@/hooks/useToggle';
import useApi from '@/hooks/useApi';
import { postPurchase } from '@/apis/checkout';
import OrderErrorModal from '../OrderErrorModal';
import CheckModal from '../CheckModal';
import { useState } from 'react';
import { useWebViewRouter } from '@/hooks/useWebViewRouter';
import { ICheckRequest, receivingType } from '@/types/checkout';
import CartItem from './CartItem';
import CartHeader from './CartHeader';
import CartResultSection from './CartResultSection';
import { WebviewAction } from '@/utils/action';
import { useKioskDeviceLocation } from '@/hooks/useKioskDeviceLocation';
import { deviceLocationType } from '@/types/data';
import { useMutation } from '@tanstack/react-query';
import PromotionCodeFormModal from '../PromotionCodeFormModal/PromotionCodeFormModal';
import PromotionCodeDeleteModal from '../PromotionCodeDeleteModal/PromotionCodeDeleteModal';
import { useSearchParams } from 'react-router-dom';
import { Image } from '@/components/Image';
import { trackEvent } from '../Event/_lib/eventTrack';

export const Cart = () => {
  const { direct } = useWebViewRouter();
  const [searchParams] = useSearchParams();
  const receivingMethod = (searchParams.get('shipping_method') ?? 'pick_up') as receivingType;
  const { handleSubmit, formState, getValues } = useFormContext<OrderSendForm>();
  const [couponCode, setCouponCode] = useState('');
  const { cartData } = useCart({ receivingMethod, couponCode });
  const [currency] = useLocalStorage('currency', null);
  const { fetchApi } = useApi();
  const [deviceLocation] = useKioskDeviceLocation();

  const [_, setLocalFormState] = useLocalStorage('local-form-state', {});
  const [isSubmit, setIsSubmit] = useState(false);
  const [errorMessage, setErrormessage] = useState('');

  // toggle
  const orderErrorModal = useToggle({ showing: false });
  const promotionCodeModal = useToggle({ showing: false });
  const checkModal = useToggle({ showing: false });
  const couponCodeDeleteModal = useToggle({ showing: false });

  const totalPayment = cartData?.total_payment ?? 0;
  const shippingFee = cartData?.shipping_fee ?? 0;

  const { isValid } = formState;
  const customerCountryCode = useWatch({ name: 'customer_country_code' });
  const shippingCountryCode = useWatch({ name: 'shipping_info_country_code' });
  const isShippingCountryCode = receivingMethod === 'pick_up' ? true : !!shippingCountryCode; // 픽업일 경우에 체크
  const agreement1 = useWatch({ name: 'service_agreement_1' });
  const agreement2 = useWatch({ name: 'service_agreement_2' });
  const disabled = !isValid || !agreement1 || !agreement2 || !customerCountryCode || !isShippingCountryCode;
  const pickupLocation: deviceLocationType = useWatch({ name: 'pickup_location' }) ?? deviceLocation;
  const kioskId: string = WebviewAction() ?? 'kioskdev';

  const { mutate, isLoading } = useMutation({
    mutationFn: async (data: OrderSendForm) => {
      const cart_offers = cartData?.cart_offers.map((i) => i.offer.id) || [];
      const email = data.customer_email_1 + '@' + data.customer_email_2;
      const shippingInfo =
        data.shipping_info_email_1 && data.shipping_info_email_2
          ? data.shipping_info_email_1 + '@' + data.shipping_info_email_2
          : '';
      const params: ICheckRequest = {
        offer_list: cart_offers,
        pick_up_location: receivingMethod === 'pick_up' ? pickupLocation : '',
        receiving_method: receivingMethod,
        kiosk_device: kioskId,
        payment: {
          offer_total: cartData?.offer_total ?? 0,
          shipping_fee: shippingFee
        },
        customer: {
          first_name: data.customer_first_name,
          last_name: data.customer_last_name,
          country_code: data.customer_country_code,
          phone_number: data.customer_phone_number,
          email
        },
        coupon_code: couponCode,
        shipping_info: {
          first_name: data.shipping_info_first_name ?? '',
          last_name: data.shipping_info_last_name ?? '',
          country_code: receivingMethod === 'domestic' ? 'KR' : data.shipping_info_country_code ?? '',
          phone_number: data.shipping_info_phone_number ?? '',
          email: shippingInfo,
          address_1: data.shipping_info_address_1 ?? '',
          address_2: data.shipping_info_address_2 ?? '',
          postal_code: data.shipping_info_postal_code ?? '',
          state: data.shipping_info_state ?? '',
          city: data.shipping_info_city ?? '',
          memo: data.shipping_info_memo ?? '',
          clearance_code: receivingMethod === 'global' ? (data.shipping_info_clearance_code ?? '') : ''
        }
      };

      const realpurchase = fetchApi((token) => postPurchase(token, params));
      return realpurchase;
    },
    onSuccess: (data: any) => {
      direct(
        `/order/success/${data?.custom_order_id}?email=${data?.customer_email}&pickupLocation=${data?.pickup_location}&offerTotal=${data?.order_total}`
      );
    },
    onError: (e: any) => {
      setIsSubmit(false);
      setErrormessage(e.message ?? e.code);
      checkModal.close();
      orderErrorModal.open();
    }
  });

  const onSubmit = (data: OrderSendForm) => {
    if (!isLoading) {
      mutate(data);
    }
  };

  return (
    <div className="w-[571px] h-screen grid grid-rows-[132px_1fr_auto] border-l border-[#f5f5f5] ">
      {/* 카트해더 */}
      <CartHeader />

      {/* 카드데이터 */}
      <div className="flex flex-col w-full h-full overflow-y-scroll">
        {cartData?.cart_offers.map((item) => <CartItem key={item.offer.id} cartItemData={item} />)}
      </div>

      {/* 쿠폰영역 */}
      {cartData?.coupon_name ? (
        <div className="text-[16px] leading-6 font-semibold text-[#ff6060] py-[14px] px-[30px] flex gap-1">
          <span>
            {cartData?.coupon_name ?? 'SUMMER2025'} -{' '}
            {
              <FormattedMessage
                id="discount_price"
                values={{ discountPrice: getPrice(cartData?.total_discount_price?.toString() ?? '0', currency) }}
                defaultMessage="{discountPrice} discount"
              />
            }
          </span>
          <Image src={'/icons/close-red.svg'} alt="close-red" onClick={couponCodeDeleteModal.open} height={16} width={16} />
        </div>
      ) : (
        <div
          className="flex items-center h-[52px] p-[30px] text-[#999999] font-semibold"
          onClick={() => promotionCodeModal.open()}
        >
          <FormattedMessage
            id="got_a_promo_code"
            values={{ discountPrice: getPrice(cartData?.total_discount_price?.toString() ?? '0', currency) }}
            defaultMessage="Got a promo code?"
          />
          <Image
            src="/icons/chevron-right-gray.svg"
            alt="arrow icon"
            className="order-page-cart-promo-code-modal-trigger-icon"
            height={20}
            width={20}
          />
        </div>
      )}

      {/* 가격정보 */}
      <CartResultSection
        totalDiscountPrice={cartData?.total_discount_price}
        offerTotal={cartData?.offer_total}
        shippingFee={cartData?.shipping_fee}
        totalPayment={cartData?.total_payment}
        discountShippingFee={cartData?.discount_shipping_fee}
        receivingMethod={receivingMethod}
      />

      {/* 하단 버튼영역 */}
      <div className="w-full h-full py-[18px] px-[30px] flex flex-row justify-center gap-4">
        {/* 뒤로가기 누르면 폼 데이터 저장 */}
        <Button
          className="!w-[132px] text-black"
          textColor="#000000"
          onClick={() => {
            setLocalFormState(getValues());
            direct('/photocard');
            trackEvent('Order_click_Back_button');
          }}
        >
          <FormattedMessage id="order_cart_back" defaultMessage="Back" />
        </Button>

        <Button
          data-primary
          disabled={disabled}
          onClick={() => {
            checkModal.open();
            trackEvent('Order_click_Order_button');
          }}
        >
          <FormattedMessage
            id="order_cart_items_checkout_button"
            values={{ orderPrice: `${getPrice(totalPayment.toString(), currency)}`, itemCount: cartData?.cart_offers.length }}
            defaultMessage="Order {orderPrice} · {itemCount} items"
          />
        </Button>
      </div>

      {/* 주문체크모달 */}
      {checkModal.isShowing && (
        <CheckModal
          onClose={checkModal.close}
          onContinueClick={handleSubmit(onSubmit)}
          total={`${getPrice(totalPayment.toString(), currency)}`}
          isSubmit={isSubmit}
          receivingMethod={receivingMethod}
        />
      )}

      {/* 주문에러모달 */}
      {orderErrorModal.isShowing && (
        <OrderErrorModal
          onAgainClick={() => {
            orderErrorModal.close();
            setErrormessage('');
          }}
          errorMessage={errorMessage}
        />
      )}

      {/* 쿠폰코드 에러모달 */}
      {promotionCodeModal.isShowing && (
        <PromotionCodeFormModal
          setCouponCode={setCouponCode}
          onClose={promotionCodeModal.close}
          onClick={() => direct('/photocard')}
          subtotal={totalPayment}
        />
      )}

      {/* 쿠폰삭제모달 */}
      {couponCodeDeleteModal.isShowing && (
        <PromotionCodeDeleteModal
          onClose={couponCodeDeleteModal.close}
          onClick={() => {
            setCouponCode('');
            couponCodeDeleteModal.close();
          }}
        />
      )}
    </div>
  );
};
